import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { Input } from '@/components/form/Input';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useMSRPCalculator } from '@/hooks/useMSRPCalculator';
import {
  useGetSubscription,
  useUpdateSubscription,
} from '@/hooks/useSubscription';
import {
  NegotiatedPriceInput,
  ScanningFrequencySelector,
  SubscriptionTierSelector,
} from '@/sections/settings/subscription/components';
import { useAuth } from '@/state/auth';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';

interface ConfigureSubscriptionProps {
  open: boolean;
  onClose: () => void;
  customerEmail?: string;
}

const inputClassName =
  'border-chariot-stroke focus:border-chariot-brand-primary focus:ring-chariot-brand-primary bg-chariot-background-primary text-chariot-text-primary w-50 rounded-md border-2 px-4 py-2 transition-all focus:ring-2 focus:ring-offset-1 focus:outline-none';

export function ConfigureSubscription({
  open,
  onClose,
  customerEmail,
}: ConfigureSubscriptionProps) {
  // State management with default values
  const { isPraetorianUser } = useAuth();

  const [localSubscription, setLocalSubscription] =
    useState<SubscriptionConfiguration>({
      estimatedCost: 0,
      attackSurface: {
        external: false,
        applications: false,
        cloud: false,
        code: false,
        internal: false,
        users: false,
        device: false,
      },
      numberOfAssets: 0,
      numberOfApplications: 0,
      scanSchedule: 'daily',
      selectedTier: 'professional',
      startDate: '',
      endDate: '',
    });

  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  const { mutateAsync: updateSubscription, status: updateSubscriptionStatus } =
    useUpdateSubscription({ email: customerEmail });

  // Get monitoring counts for actual asset and application totals
  const { data: monitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
  });

  // Calculate total assets from monitoring data (React 19: simple transformations don't need useMemo)
  const actualTotalAssets = !monitoringCounts
    ? 0
    : (() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { applications: _applications, ...restMonitoringCounts } =
          monitoringCounts;

        return Object.keys(restMonitoringCounts).reduce((acc, curr) => {
          const category =
            monitoringCounts[curr as keyof typeof monitoringCounts];
          if (typeof category === 'object' && category !== null) {
            const categoryTotal = Object.values(category).reduce<number>(
              (sum, value) => {
                if (typeof value === 'object' && value !== null) {
                  return (
                    sum +
                    Object.values(value).reduce<number>(
                      (s, v) => s + (v as number),
                      0
                    )
                  );
                }
                return sum + (value as number);
              },
              0
            );
            return acc + categoryTotal;
          }
          return acc;
        }, 0);
      })();

  // React 19: Simple property access doesn't need useMemo
  const actualTotalApplications = monitoringCounts?.applications?.webseed || 0;

  // Calculate Usage Price based on actual current usage
  const { msrp: usagePrice, breakdown: usageBreakdown } = useMSRPCalculator({
    numberOfApplications: actualTotalApplications,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: actualTotalAssets,
    supportTier: localSubscription.selectedTier,
  });

  // Calculate MSRP based on coverage limits
  const { msrp, breakdown } = useMSRPCalculator({
    numberOfApplications: localSubscription.numberOfApplications,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: localSubscription.numberOfAssets,
    supportTier: localSubscription.selectedTier,
  });

  const handleSubscriptionChange = useCallback(
    (value: Partial<SubscriptionConfiguration>) => {
      setLocalSubscription(prev => ({ ...prev, ...value }));
    },
    []
  );

  const handleAttackSurfaceChange = useCallback(
    (
      newAttackSurface: import('@/components/AttackSurfaceSelector').AttackSurfaceSelection
    ) => {
      setLocalSubscription(prev => ({
        ...prev,
        // Convert AttackSurfaceSelection to Record<string, boolean> safely
        attackSurface: { ...newAttackSurface } as Record<string, boolean>,
      }));
    },
    []
  );

  const handleAttackSurfaceSelectionChange = useCallback(
    (
      key: keyof import('@/components/AttackSurfaceSelector').AttackSurfaceSelection,
      selected: boolean
    ) => {
      // When Web Applications is disabled, reset the number of applications
      if (key === 'applications' && !selected) {
        handleSubscriptionChange({ numberOfApplications: 0 });
      }
    },
    [handleSubscriptionChange]
  );

  // Memoized save handler
  const handleSave = useCallback(async () => {
    // Validate Web FQDNs when web attack surface is enabled
    if (
      localSubscription.attackSurface.applications &&
      localSubscription.numberOfApplications === 0
    ) {
      toast.error(
        '# of Web FQDNs cannot be 0 when Web attack surface is enabled'
      );
      return;
    }
    // Include MSRP in saved configuration if Praetorian user
    const configToSave = isPraetorianUser
      ? { ...localSubscription, msrp, msrpBreakdown: breakdown }
      : localSubscription;
    await updateSubscription(configToSave);
    onClose();
  }, [
    localSubscription,
    updateSubscription,
    onClose,
    isPraetorianUser,
    msrp,
    breakdown,
  ]);

  // Sync local subscription state when server data changes
  // Using subscriptionStatus as a stable trigger - when status changes from loading to success,
  // we know we have fresh data. This avoids JSON.stringify which breaks memoization.
  useEffect(() => {
    if (subscriptionValue && subscriptionStatus === 'success') {
      setLocalSubscription(_prev => ({
        ...subscriptionValue,
        attackSurface: {
          external: subscriptionValue.attackSurface?.external ?? false,
          applications: subscriptionValue.attackSurface?.applications ?? false,
          cloud: subscriptionValue.attackSurface?.cloud ?? false,
          code: subscriptionValue.attackSurface?.code ?? false,
          internal: subscriptionValue.attackSurface?.internal ?? false,
          users: subscriptionValue.attackSurface?.users ?? false,
          device: subscriptionValue.attackSurface?.device ?? false,
        },
        startDate: subscriptionValue.startDate || '',
        endDate: subscriptionValue.endDate || '',
      }));
    }
  }, [subscriptionStatus, subscriptionValue]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Subscription"
      size="7xl"
      disableAutoFocus={true}
      footer={{
        isLoading: updateSubscriptionStatus === 'pending',
        text: 'Save',
        className: 'w-fit',
        onClick: handleSave,
      }}
      // className="max-h-[90vh] overflow-y-auto"
      data-testid="configure-subscription-modal"
      aria-labelledby="configure-subscription-title"
    >
      <Loader
        type="spinner"
        isLoading={subscriptionStatus === 'pending' && !subscriptionValue}
      >
        <div
          className="space-y-8"
          role="main"
          aria-label="Subscription configuration form"
        >
          {/* Subscription Term Section */}
          <section className="space-y-4" aria-labelledby="term-section-title">
            <h3
              id="term-section-title"
              className="text-chariot-text-primary text-lg font-semibold"
            >
              Subscription Term
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <StyledDatePicker
                label="Start Date"
                name="start-date"
                value={localSubscription.startDate || ''}
                onChange={value =>
                  handleSubscriptionChange({ startDate: value })
                }
                max={localSubscription.endDate}
              />
              <StyledDatePicker
                label="End Date"
                name="end-date"
                value={localSubscription.endDate || ''}
                onChange={value => handleSubscriptionChange({ endDate: value })}
                min={localSubscription.startDate}
              />
            </div>
          </section>

          {/* Attack Surface Coverage Section */}
          <section className="space-y-4">
            <h3 className="text-chariot-text-primary text-lg font-semibold">
              Attack Surface Coverage
            </h3>
            <AttackSurfaceSelector
              value={{
                external: localSubscription.attackSurface.external || false,
                applications:
                  localSubscription.attackSurface.applications || false,
                internal: localSubscription.attackSurface.internal || false,
                cloud: localSubscription.attackSurface.cloud || false,
                code: localSubscription.attackSurface.code || false,
                users: localSubscription.attackSurface.users || false,
                device: localSubscription.attackSurface.device || false,
              }}
              onChange={handleAttackSurfaceChange}
              onSelectionChange={handleAttackSurfaceSelectionChange}
              requiredOptions={['external']}
            />
          </section>

          {/* Scanning Frequency Section */}
          <ScanningFrequencySelector
            selectedFrequency={
              localSubscription.scanSchedule as 'daily' | 'weekly' | 'monthly'
            }
            onFrequencyChange={frequency =>
              handleSubscriptionChange({ scanSchedule: frequency })
            }
          />

          {/* Subscription Tier Section */}
          <SubscriptionTierSelector
            selectedTier={localSubscription.selectedTier}
            onTierChange={tier => {
              // Professional and Ultimate tiers include web attack surface
              const isProfessionalOrUltimate =
                tier === 'professional' || tier === 'ultimate';
              const updatedAttackSurface = isProfessionalOrUltimate
                ? {
                    ...localSubscription.attackSurface,
                    applications: true,
                  }
                : localSubscription.attackSurface;

              // Set default 50 FQDNs for Professional and Ultimate tiers
              // Only set if current value is 0 (don't override existing values)
              const updatedNumberOfApplications =
                isProfessionalOrUltimate &&
                localSubscription.numberOfApplications === 0
                  ? 50
                  : localSubscription.numberOfApplications;

              handleSubscriptionChange({
                selectedTier: tier,
                attackSurface: updatedAttackSurface,
                numberOfApplications: updatedNumberOfApplications,
              });
            }}
            tiers={['freemium', 'foundational', 'professional', 'ultimate']}
          />

          {/* Pricing Sections */}
          {isPraetorianUser ? (
            <>
              {/* Usage Price Section */}
              <section className="space-y-4">
                <h3 className="text-chariot-text-primary text-lg font-semibold">
                  Usage Price
                </h3>
                <Container className="bg-chariot-background-primary">
                  {/* Current Usage (Read-only) */}
                  <div className="mb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-chariot-text-primary text-sm">
                        Current Asset Usage
                      </label>
                      <Input
                        name="currentAssets"
                        value={actualTotalAssets.toLocaleString()}
                        onChange={() => {}}
                        disabled
                        className={cn(
                          inputClassName,
                          'w-32 cursor-not-allowed text-right opacity-60'
                        )}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-chariot-text-primary text-sm">
                        Current Application Usage
                      </label>
                      <Input
                        name="currentApplications"
                        value={actualTotalApplications.toLocaleString()}
                        onChange={() => {}}
                        disabled
                        className={cn(
                          inputClassName,
                          'w-32 cursor-not-allowed text-right opacity-60'
                        )}
                      />
                    </div>
                  </div>

                  {/* Usage Price below */}
                  <div className="border-chariot-stroke flex items-start justify-between border-t pt-4">
                    <div>
                      <h2 className="text-chariot-text-primary mb-2 flex items-center gap-2 text-sm font-medium">
                        Usage Price
                        <Tooltip title="Auto-Calculated Based on Current Coverage">
                          <InformationCircleIcon className="text-chariot-text-secondary h-4 w-4" />
                        </Tooltip>
                      </h2>
                      {usageBreakdown && (
                        <div className="text-chariot-text-secondary mt-3 text-xs">
                          Platform Fee: $
                          {usageBreakdown.platformFee.toLocaleString()},
                          Application Scanning: $
                          {usageBreakdown.fqdnCost.toLocaleString()}, Asset
                          Coverage: ${usageBreakdown.assetCost.toLocaleString()}
                          , Support Tier: $
                          {usageBreakdown.supportTier.toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="text-chariot-text-primary text-right text-2xl font-bold">
                      ${usagePrice.toLocaleString()}
                    </div>
                  </div>
                </Container>
              </section>

              {/* MSRP Price Section */}
              <section className="space-y-4">
                <h3 className="text-chariot-text-primary text-lg font-semibold">
                  MSRP Price
                </h3>
                <Container className="bg-chariot-background-primary">
                  {/* Coverage Limits */}
                  <div className="mb-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-chariot-text-primary text-sm">
                        Asset Coverage Limit
                      </label>
                      <Input
                        name="numberOfAssets"
                        value={localSubscription.numberOfAssets.toString()}
                        onChange={e => {
                          setLocalSubscription(prev => ({
                            ...prev,
                            numberOfAssets: parseInt(e.target.value) || 0,
                          }));
                        }}
                        className={cn(inputClassName, 'w-32 text-right')}
                        placeholder="Enter limit"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-chariot-text-primary text-sm">
                        Application Coverage Limit
                      </label>
                      <Input
                        name="numberOfApplications"
                        value={localSubscription.numberOfApplications.toString()}
                        disabled={!localSubscription.attackSurface.applications}
                        onChange={e => {
                          setLocalSubscription(prev => ({
                            ...prev,
                            numberOfApplications: parseInt(e.target.value) || 0,
                          }));
                        }}
                        className={cn(inputClassName, 'w-32 text-right')}
                        placeholder="Enter limit"
                      />
                    </div>
                  </div>

                  {/* MSRP Price Display */}
                  <div className="border-chariot-stroke flex items-start justify-between border-t pt-4">
                    <div>
                      <h2 className="text-chariot-text-primary mb-2 flex items-center gap-2 text-sm font-medium">
                        MSRP Price
                        <Tooltip title="Auto-Calculated Based on Coverage Limits">
                          <InformationCircleIcon className="text-chariot-text-secondary h-4 w-4" />
                        </Tooltip>
                      </h2>
                      {breakdown && (
                        <div className="text-chariot-text-secondary mt-3 text-xs">
                          Platform Fee: $
                          {breakdown.platformFee.toLocaleString()}, Application
                          Scanning: ${breakdown.fqdnCost.toLocaleString()},
                          Asset Coverage: $
                          {breakdown.assetCost.toLocaleString()}, Support Tier:
                          ${breakdown.supportTier.toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div className="text-chariot-text-primary text-right text-2xl font-bold">
                      ${msrp.toLocaleString()}
                    </div>
                  </div>
                </Container>
              </section>

              {/* Negotiated Price Section */}
              <NegotiatedPriceInput
                value={localSubscription.estimatedCost}
                onChange={value =>
                  handleSubscriptionChange({ estimatedCost: value })
                }
                msrp={msrp}
                usagePrice={usagePrice}
              />
            </>
          ) : (
            /* Regular User - Single Estimated Price */
            <Container
              isSelected
              className="bg-chariot-background-primary flex items-center justify-between"
            >
              <h2
                id="price-section-title"
                className="text-chariot-text-primary text-sm font-medium"
              >
                Estimated Annual Price
              </h2>
              <div className="text-chariot-brand-primary text-2xl font-bold">
                <Input
                  name="estimatedCost"
                  className={cn(inputClassName, 'text-right')}
                  value={localSubscription.estimatedCost.toString()}
                  onChange={e =>
                    handleSubscriptionChange({
                      estimatedCost: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter price"
                />
              </div>
            </Container>
          )}
        </div>
      </Loader>
    </Modal>
  );
}
